<template>
  <div
    v-if="isOpen"
    class="fixed inset-0 z-50 flex items-center justify-center"
    @click.self="close">
    <!-- Backdrop -->
    <div class="absolute inset-0 bg-black bg-opacity-50" />

    <!-- Modal -->
    <div class="relative bg-white rounded-lg border-2 border-gray-900 w-full max-w-md mx-4 shadow-xl">
      <!-- Header -->
      <div class="flex items-center justify-between p-4 border-b border-gray-200">
        <h3 class="text-lg font-semibold text-gray-900">
          {{ isEditing ? 'Edit Goal' : 'Set Goal' }}
        </h3>
        <button
          @click="close"
          class="text-gray-400 hover:text-gray-600">
          <svg class="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>
      </div>

      <!-- Body -->
      <div class="p-4">
        <!-- Goal Type Selector -->
        <div class="mb-4">
          <label class="block text-xs text-gray-500 uppercase tracking-wider font-medium mb-2">
            Goal Type
          </label>
          <div class="grid grid-cols-2 gap-2">
            <button
              v-for="type in goalTypes"
              :key="type.value"
              @click="selectType(type.value)"
              :disabled="isEditing"
              :class="[
                'p-3 rounded-lg border-2 text-left transition-all',
                form.goal_type === type.value
                  ? 'border-gray-900 bg-gray-50'
                  : 'border-gray-200 hover:border-gray-300',
                isEditing ? 'opacity-60 cursor-not-allowed' : ''
              ]">
              <div class="flex items-center mb-1">
                <svg class="w-4 h-4 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path
                    v-if="type.value === 'mindfulness_minutes'"
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    stroke-width="2"
                    d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z" />
                  <path
                    v-else
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    stroke-width="2"
                    d="M15.536 8.464a5 5 0 010 7.072m2.828-9.9a9 9 0 010 12.728M5.586 15H4a1 1 0 01-1-1v-4a1 1 0 011-1h1.586l4.707-4.707C10.923 3.663 12 4.109 12 5v14c0 .891-1.077 1.337-1.707.707L5.586 15z" />
                </svg>
                <span class="text-sm font-medium">{{ type.label }}</span>
              </div>
              <span class="text-xs text-gray-500">{{ type.description }}</span>
            </button>
          </div>
        </div>

        <!-- Target Value -->
        <div class="mb-4">
          <label class="block text-xs text-gray-500 uppercase tracking-wider font-medium mb-2">
            Target Value
          </label>
          <div class="relative">
            <input
              v-model.number="form.target_value"
              type="number"
              :min="currentRange.min"
              :max="currentRange.max"
              :step="currentRange.step"
              class="w-full px-4 py-3 border-2 border-gray-200 rounded-lg focus:border-gray-900 focus:outline-none text-lg font-medium"
              :placeholder="String(currentRange.default)">
            <span class="absolute right-4 top-1/2 -translate-y-1/2 text-gray-400 text-sm">
              {{ currentRange.unit }}
            </span>
          </div>

          <!-- Range Info -->
          <div class="flex justify-between mt-2 text-xs text-gray-400">
            <span>Min: {{ currentRange.min }} {{ currentRange.unit }}</span>
            <span>Default: {{ currentRange.default }} {{ currentRange.unit }}</span>
            <span>Max: {{ currentRange.max }} {{ currentRange.unit }}</span>
          </div>
        </div>

        <!-- Validation Error -->
        <div v-if="validationError" class="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg">
          <p class="text-sm text-red-600">
            {{ validationError }}
          </p>
        </div>

        <!-- Current Goal Info (when editing) -->
        <div v-if="isEditing && goal" class="mb-4 p-3 bg-gray-50 rounded-lg">
          <p class="text-xs text-gray-500 mb-1">
            Current Goal
          </p>
          <p class="text-sm font-medium">
            {{ goal.target_value }} {{ currentRange.unit }}
          </p>
        </div>
      </div>

      <!-- Footer -->
      <div class="flex items-center justify-between p-4 border-t border-gray-200 bg-gray-50 rounded-b-lg">
        <!-- Delete Button (when editing) -->
        <div>
          <button
            v-if="isEditing"
            @click="confirmDelete"
            :disabled="saving"
            class="text-red-600 hover:text-red-700 text-sm font-medium disabled:opacity-50">
            Delete Goal
          </button>
        </div>

        <!-- Action Buttons -->
        <div class="flex space-x-3">
          <button
            @click="close"
            :disabled="saving"
            class="px-4 py-2 text-sm font-medium text-gray-700 hover:text-gray-900 disabled:opacity-50">
            Cancel
          </button>
          <button
            @click="save"
            :disabled="!isValid || saving"
            class="px-4 py-2 bg-gray-900 text-white rounded-lg text-sm font-medium hover:bg-gray-800 disabled:opacity-50 disabled:cursor-not-allowed">
            {{ saving ? 'Saving...' : (isEditing ? 'Update' : 'Create') }}
          </button>
        </div>
      </div>
    </div>

    <!-- Delete Confirmation Dialog -->
    <div
      v-if="showDeleteConfirm"
      class="fixed inset-0 z-60 flex items-center justify-center"
      @click.self="showDeleteConfirm = false">
      <div class="absolute inset-0 bg-black bg-opacity-25" />
      <div class="relative bg-white rounded-lg border-2 border-gray-900 p-6 max-w-sm mx-4">
        <h4 class="text-lg font-semibold mb-2">
          Delete Goal?
        </h4>
        <p class="text-sm text-gray-600 mb-4">
          This will permanently delete your {{ selectedTypeLabel }} goal.
        </p>
        <div class="flex justify-end space-x-3">
          <button
            @click="showDeleteConfirm = false"
            class="px-4 py-2 text-sm font-medium text-gray-700">
            Cancel
          </button>
          <button
            @click="deleteGoal"
            :disabled="saving"
            class="px-4 py-2 bg-red-600 text-white rounded-lg text-sm font-medium hover:bg-red-700 disabled:opacity-50">
            Delete
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'GoalsModal',

  props: {
    isOpen: {
      type: Boolean,
      default: false
    },
    goal: {
      type: Object,
      default: null
    },
    existingGoals: {
      type: Array,
      default: () => []
    }
  },

  emits: ['close', 'save', 'delete'],

  data() {
    return {
      form: {
        goal_type: 'mindfulness_minutes',
        target_value: null
      },
      saving: false,
      showDeleteConfirm: false,
      validationError: null,
      goalTypes: [
        {
          value: 'mindfulness_minutes',
          label: 'Mindfulness',
          description: 'Daily meditation minutes'
        },
        {
          value: 'audio_limit',
          label: 'Audio Limit',
          description: 'Max safe exposure (dB)'
        }
      ],
      ranges: {
        mindfulness_minutes: {
          min: 5,
          max: 60,
          default: 10,
          step: 1,
          unit: 'min'
        },
        audio_limit: {
          min: 70,
          max: 100,
          default: 85,
          step: 1,
          unit: 'dB'
        }
      }
    }
  },

  computed: {
    isEditing() {
      return !!this.goal
    },
    currentRange() {
      return this.ranges[this.form.goal_type] || this.ranges.mindfulness_minutes
    },
    selectedTypeLabel() {
      const type = this.goalTypes.find((t) => t.value === this.form.goal_type)
      return type ? type.label : ''
    },
    isValid() {
      if (!this.form.goal_type) return false
      const value = this.form.target_value
      if (value === null || value === '') return false
      const range = this.currentRange
      return value >= range.min && value <= range.max
    },
    availableTypes() {
      if (this.isEditing) return this.goalTypes
      const existingTypes = this.existingGoals.map((g) => g.goal_type)
      return this.goalTypes.filter((t) => !existingTypes.includes(t.value))
    }
  },

  watch: {
    isOpen: {
      immediate: true,
      handler(open) {
        if (open) {
          this.initForm()
        }
      }
    },
    'form.goal_type': {
      handler() {
        this.validationError = null
        if (!this.isEditing && this.form.target_value === null) {
          this.form.target_value = this.currentRange.default
        }
      }
    },
    'form.target_value': {
      handler() {
        this.validateValue()
      }
    }
  },

  methods: {
    initForm() {
      this.saving = false
      this.showDeleteConfirm = false
      this.validationError = null

      if (this.goal) {
        // Editing existing goal
        this.form.goal_type = this.goal.goal_type
        this.form.target_value = parseFloat(this.goal.target_value)
      } else {
        // Creating new goal
        const available = this.availableTypes
        if (available.length > 0) {
          this.form.goal_type = available[0].value
          this.form.target_value = this.ranges[available[0].value].default
        } else {
          this.form.goal_type = 'mindfulness_minutes'
          this.form.target_value = this.ranges.mindfulness_minutes.default
        }
      }
    },

    selectType(type) {
      if (this.isEditing) return
      this.form.goal_type = type
      this.form.target_value = this.ranges[type].default
    },

    validateValue() {
      const value = this.form.target_value
      const range = this.currentRange

      if (value === null || value === '') {
        this.validationError = null
        return
      }

      if (value < range.min) {
        this.validationError = `Minimum value is ${range.min} ${range.unit}`
      } else if (value > range.max) {
        this.validationError = `Maximum value is ${range.max} ${range.unit}`
      } else {
        this.validationError = null
      }
    },

    async save() {
      if (!this.isValid || this.saving) return

      this.saving = true
      try {
        this.$emit('save', {
          id: this.goal?.id,
          goal_type: this.form.goal_type,
          target_value: this.form.target_value
        })
      } finally {
        this.saving = false
      }
    },

    confirmDelete() {
      this.showDeleteConfirm = true
    },

    async deleteGoal() {
      if (!this.goal || this.saving) return

      this.saving = true
      try {
        this.$emit('delete', this.goal.id)
      } finally {
        this.saving = false
        this.showDeleteConfirm = false
      }
    },

    close() {
      if (this.saving) return
      this.$emit('close')
    }
  }
}
</script>
